<template>
  <section id="content-section">
    <Card>
      <div class="tw-flex tw-items-center tw-justify-between">
        <h2 class="tw-heading-2">Content Library</h2>
        <AddContentLibrary
          v-if="
            hasObjectPermissions(
              $page.props.mentor,
              OBJECT_PERMISSIONS.MENTORS.OWNER,
            )
          "
          :actions="actions"
        />
      </div>
      <MentorContentLibraryEmptyState
        v-if="
          !hasAnyContent &&
          hasObjectPermissions(
            $page.props.mentor,
            OBJECT_PERMISSIONS.MENTORS.OWNER,
          )
        "
        :actions="actions"
      />
      <div v-else>
        <ul
          class="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-2 xs:tw-grid-cols-2 md:tw-flex md:tw-items-center md:tw-gap-0 md:tw-space-x-2"
        >
          <li v-for="action in availableActions" :key="action.value">
            <button
              class="tw-group tw-flex tw-w-full tw-items-center tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition-colors hover:tw-bg-neutral-100 focus:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-neutral-100"
              :class="[
                isActive(action.value)
                  ? 'tw-bg-neutral-50'
                  : 'tw-text-neutral-500',
              ]"
              @click="selectAction(action.value)"
            >
              <component
                class="tw-mr-2 tw-w-4 tw-text-primary-500"
                :is="action.icon"
              />
              {{ action.label }}
              <span
                class="tw-ml-2 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-neutral-50 tw-text-sm tw-transition-colors group-hover:tw-bg-neutral-100"
              >
                {{ action.elements.length }}
              </span>
            </button>
          </li>
        </ul>
        <div class="tw-mt-6">
          <div v-if="!selectedActionObject.elements.length">
            Nothing found in this section.
          </div>
          <div v-else class="tw-space-y-4">
            <component
              v-for="element in selectedActionObject.elements"
              :key="element.value"
              :is="selectedActionObject.elementComponent"
              :element="element"
            />
          </div>
        </div>
      </div>
    </Card>
  </section>
</template>

<script setup>
import Card from '@/shared/components/Card.vue'
import MentorContentLibraryEmptyState from './MentorContentLibraryEmptyState.vue'
import FloatingNibIcon from '@/assets/images/colored-icons/floating-nib.svg'
import MicrophoneIcon from '@/assets/images/colored-icons/microphone.svg'
import RulerAndPenIcon from '@/assets/images/colored-icons/ruler-and-pen.svg'
import DocumentsIcon from '@/assets/images/colored-icons/documents.svg'
import AddContentLibrary from './AddContentLibrary.vue'
import MentorContentLibraryMentorColumnCard from './MentorContentLibraryMentorColumnCard.vue'
import MentorContentLibraryVideoLibraryCard from './MentorContentLibraryVideoLibraryCard.vue'
import MentorContentLibraryPodcastCard from './MentorContentLibraryPodcastCard.vue'
import MentorContentLibraryGlossaryCard from './MentorContentLibraryGlossaryCard.vue'
import { computed, onBeforeMount, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useEditProfileModeInjector } from '@/shared/composables/edit-profile-mode/useEditProfileModeInjector.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { OBJECT_PERMISSIONS } from '@/shared/data/permissionSystem.js'

defineProps({
  hasAnyContent: {
    type: Boolean,
    default: false,
  },
})

const page = usePage()
const { isEditModeEnabled } = useEditProfileModeInjector()
const { hasObjectPermissions } = usePermissionSystem()

const selectedAction = ref('mentor-column')
const actions = [
  {
    label: 'Mentor Column',
    icon: FloatingNibIcon,
    value: 'mentor-column',
    elements: page.props.mentorColumns,
    elementComponent: MentorContentLibraryMentorColumnCard,
    formLink: page.props.contentLibraryLinks.mentorColumn,
  },
  {
    label: 'Video Library',
    icon: RulerAndPenIcon,
    value: 'video-library',
    elements: page.props.videos,
    elementComponent: MentorContentLibraryVideoLibraryCard,
    formLink: page.props.contentLibraryLinks.videoLibrary,
  },
  {
    label: 'Podcast',
    icon: MicrophoneIcon,
    value: 'podcast',
    elements: page.props.podcasts,
    elementComponent: MentorContentLibraryPodcastCard,
    formLink: page.props.contentLibraryLinks.podcast,
  },
  {
    label: 'Glossary',
    icon: DocumentsIcon,
    value: 'glossaries',
    elements: page.props.glossaries,
    elementComponent: MentorContentLibraryGlossaryCard,
    formLink: page.props.contentLibraryLinks.glossary,
  },
]

const selectedActionObject = computed(() => {
  return actions.find((action) => action.value === selectedAction.value)
})

const availableActions = computed(() => {
  return actions.filter((action) => {
    return action.elements.length > 0 || isEditModeEnabled.value
  })
})

const selectAction = (action) => {
  selectedAction.value = action
}

const isActive = (action) => {
  return selectedAction.value === action
}

onBeforeMount(() => {
  const action = actions.find((action) => action.elements.length > 0)
  if (action) selectedAction.value = action.value
})
</script>
